// ================= CREDITS: REPORTING A BOUT, AND WHAT IT PAID =================
//
// deploy_handoff/accounts-stage2-scope.md §6. The only file in js/ that knows /api/bouts exists.
//
// QUEUE FIRST, SUBMIT SECOND, and never the other way round. A bout ends at the moment a player is
// least willing to wait: they want the result screen, not a spinner. So the bout is written to
// localStorage synchronously and the network is attempted afterwards — if it fails, if the tab is
// closed, if the player was on a train, the bout is still there and goes up on the next flush. The
// same local-first posture js/deck-sync.js takes for decks, for the same reason: the player's own
// device is the thing that is always available.
//
// THE SERVER'S NUMBER WINS. js/credit-rules.js lets the result screen say "+1" immediately, but the
// award actually banked is whatever /api/bouts returns — it applies the ceiling, and it is the only
// side that can see the other devices this player has been using today.
//
// NOTHING HERE GATES ON PROTOCOL. Like deck-sync, this reports what it finds and lets the UI decide
// not to ask; a hard `file://` check would be permanently on in jsdom, which is exactly where the
// rules get proved.
window.Credits = (function () {
  'use strict';

  const QUEUE_KEY = 'sc.credits.queue.v1';
  // A bounded queue, because an unbounded one is a way to fill a player's storage quota with bouts
  // that will never be accepted. The OLDEST are dropped rather than the newest: a bout from three
  // weeks offline is worth less than the one just played, and something has to give.
  const MAX_QUEUE = 100;

  function storage() {
    try { return window.localStorage; } catch (e) { return null; }
  }

  function readQueue() {
    const s = storage();
    if (!s) return [];
    try {
      const raw = s.getItem(QUEUE_KEY);
      const p = raw ? JSON.parse(raw) : null;
      return Array.isArray(p) ? p.filter(x => x && typeof x.id === 'string') : [];
    } catch (e) { return []; }
  }

  function writeQueue(list) {
    const s = storage();
    if (!s) return false;
    try {
      s.setItem(QUEUE_KEY, JSON.stringify(list.slice(-MAX_QUEUE)));
      return true;
    } catch (e) { return false; }
  }

  function newBoutId() {
    const g = window.crypto && window.crypto.getRandomValues;
    if (g) {
      const buf = new Uint32Array(3);
      window.crypto.getRandomValues(buf);
      return 'b_' + (buf[0].toString(36) + buf[1].toString(36) + buf[2].toString(36)).slice(0, 16);
    }
    return 'b_' + Date.now().toString(36) + Math.floor(Math.random() * 1e6).toString(36);
  }

  // WHICH SEAT IS THE PERSON HOLDING THE DEVICE.
  //
  // Returns null for hotseat, and that is a rule rather than a gap: two people sharing one screen
  // means every bout has a winner who is also the loser's own account, so "the player won" has no
  // referent. A hotseat bout is played, enjoyed, and paid nothing. It is also not in the public
  // build (tools/build-dist.js's playtest profile ships { online: true, hotseat: false }), so this
  // is the local-play case: the seat that is not the device's owner belongs to an AI or to the
  // other end of a socket, never to a second person on this screen.
  function playerSeat(session) {
    if (!session) return null;
    if (session.p2IsAI) return 'p1';
    if (session.netRole === 'host') return 'p1';
    if (session.netRole === 'guest') return 'p2';
    return null;
  }

  // The submission, built from the logger's record rather than from the live state — the record is
  // the thing that already carries the seed and both deck ids, and it is the same object Stage 3
  // would replay from.
  function submissionFor(session) {
    const seat = playerSeat(session);
    if (!seat) return null;
    const rec = session.logger && session.logger.getRecord ? session.logger.getRecord() : null;
    if (!rec || !rec.result) return null;

    const winner = rec.result.winner || null;
    const result = winner === null ? 'draw' : (winner === seat ? 'win' : 'loss');
    const opp = seat === 'p1' ? 'p2' : 'p1';

    // A Daily Challenge bout carries the day and tier it was played at. Nothing else about it is
    // sent: the opponent deck, the seed and the award all fall out of the date on the server, so
    // this is a label saying WHICH challenge, not a claim about what it was worth.
    const daily = session.daily
      ? { day: session.daily.day, tier: session.daily.tier }
      : null;

    return {
      id: newBoutId(),
      ...(daily ? { daily } : {}),
      seed: rec.seed || null,
      deckId: (rec.fighters[seat] && rec.fighters[seat].deckId) || null,
      opponentDeckId: (rec.fighters[opp] && rec.fighters[opp].deckId) || null,
      result: result,
      exchanges: rec.result.exchangeCount || 0,
      startedAt: rec.startedAt || null,
      endedAt: rec.endedAt || null
    };
  }

  // Called the moment a bout ends. Synchronous and storage-only: returns what the client BELIEVES
  // the bout is worth, for the result screen, with no promise that it has been banked.
  function record(session) {
    const sub = submissionFor(session);
    if (!sub) return { queued: false, result: null, expected: 0 };
    const q = readQueue();
    q.push(sub);
    const queued = writeQueue(q);
    const rules = window.CREDIT_RULES;
    // What the client BELIEVES this is worth, before the server applies the day's ceiling.
    //
    // A Daily Challenge win is worth its tier — and worth NOTHING if that tier has already been
    // beaten today, because retries are unlimited and only the first win pays. Promising 15 for a
    // rematch would be the result screen telling a player something the ledger is about to
    // contradict, which is the one lie this path can tell.
    let expected = rules ? rules.awardFor(sub.result) : 0;
    if (session.daily && sub.result === 'win') {
      expected = session.daily.paysOut === false ? 0 : (session.daily.award || 0);
    }
    // AND CLAMPED BY THE DAY'S CEILING, which this screen used to ignore entirely. The tier award
    // was the whole of the promise, so a player at 45 of 50 was told "+15 credits" for a Gold win
    // that could only ever pay 5. The server now pays the 5 rather than nothing, but the sentence
    // was still wrong by ten.
    //
    // The room comes from the server (see boutSetup) and may be stale by one bout on another
    // device, so this is an EXPECTATION and not a receipt -- js/ui.js repaints the line with the
    // settled amount as soon as the flush answers. A null means the server never said, and an
    // unclamped promise is the honest fallback: it is what this screen said before the field
    // existed, and inventing a ceiling from a day the client cannot date would be worse.
    const room = session.daily ? session.daily.remaining : null;
    if (typeof room === 'number' && expected > room) expected = Math.max(0, room);
    // The id travels back with it so the caller can match the settled amount to THIS bout rather
    // than to whatever else the queue happened to be holding.
    return { queued: queued, result: sub.result, expected: expected, id: sub.id };
  }

  // WHAT ONE QUEUED BOUT LOOKS LIKE IT IS WORTH, read back off the entry rather than stored on it.
  //
  // deploy_handoff/retention-plan.md R2 assumed the queue already carried the `expected` the result
  // screen showed. It does not: `record()` computes that for its return value and pushes the bare
  // submission. Recomputing here rather than adding the field is the smaller change and the safer
  // one — a client-computed award written into the queue is a client-computed award that gets
  // POSTed with it, and this endpoint's whole posture is that the client does not get a vote on
  // what it is paid. It also reprices old entries for free if the table ever moves.
  //
  // A daily win is worth its tier. It can OVERSTATE in exactly one case — a queued win at a tier
  // already beaten that day, which the server pays nothing for — and that, along with the 50-a-day
  // ceiling applied at flush, is why the sentence this feeds says "Up to".
  function expectedFor(entry) {
    const rules = window.CREDIT_RULES;
    if (!rules || !entry) return 0;
    const tier = entry.daily && entry.daily.tier;
    if (entry.result === 'win' && tier && rules.DAILY_TIERS[tier] != null) return rules.DAILY_TIERS[tier];
    // Read through the table rather than returning a literal 0 for a loss: the fact that a loss
    // pays nothing lives in one place, and this is not it.
    return rules.awardFor(entry.result);
  }

  function queuedTotal() {
    return readQueue().reduce((n, e) => n + expectedFor(e), 0);
  }

  async function postJson(url, body) {
    const res = await window.fetch(url, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    // CONTENT-TYPE, NOT STATUS — the same trap js/deck-sync.js documents. A deploy that lost its
    // Functions answers /api/ with the site's own HTML, and a status check calls that a success.
    const type = res.headers.get('Content-Type') || '';
    if (!/json/i.test(type)) throw new Error('api_unavailable');
    return { status: res.status, body: await res.json() };
  }

  async function getJson(url) {
    const res = await window.fetch(url, { credentials: 'same-origin' });
    const type = res.headers.get('Content-Type') || '';
    if (!/json/i.test(type)) throw new Error('api_unavailable');
    return { status: res.status, body: await res.json() };
  }

  // Sends whatever is queued, oldest first, and keeps only what is worth sending again.
  //
  // THREE OUTCOMES AND THEY ARE NOT THE SAME. A bout is dropped from the queue when the server has
  // it (awarded, capped, or duplicate — all mean "recorded") and when the server refuses it as
  // malformed, because resending a 400 forever is a queue that never empties. It is KEPT only when
  // the failure was transport or session: those are the cases where trying again later works, and
  // dropping them would silently lose a credit the player earned.
  async function flush() {
    const q = readQueue();
    if (!q.length) return { sent: 0, awarded: 0, settled: {}, kept: 0, ok: true };

    let sent = 0, awarded = 0;
    // PER BOUT AS WELL AS IN TOTAL. `awarded` is the sum over everything the queue held, which is
    // the right number for a top-bar refresh and the wrong one for a result screen: a flush that
    // clears three offline bouts would print all three bouts' credits under the one that just
    // ended. Keyed by bout id so the caller can ask about the one it cares about.
    const settled = Object.create(null);
    const keep = [];
    for (let i = 0; i < q.length; i++) {
      let r;
      try {
        r = await postJson('/api/bouts', q[i]);
      } catch (e) {
        // Transport failure: everything from here on stays queued, and we stop rather than
        // hammering an endpoint that is not answering.
        for (let j = i; j < q.length; j++) keep.push(q[j]);
        writeQueue(keep);
        return { sent, awarded, settled, kept: keep.length, ok: false, error: 'api_unavailable' };
      }
      // EVERY 5xx, not just the 503 the endpoint returns deliberately. A server fault is transient
      // by definition, and the specific one that made this a real case rather than a careful one:
      // deploying the Functions before applying the migration they need makes every submission a
      // 500 until the migration runs. Dropping those would throw away real credits during a window
      // that is entirely the deploy's fault, and the player would never know there was anything to
      // ask about. 401 joins them because signing in is a thing that happens next.
      if (r.status === 401 || r.status >= 500) {
        for (let j = i; j < q.length; j++) keep.push(q[j]);
        writeQueue(keep);
        return { sent, awarded, settled, kept: keep.length, ok: false,
                 error: (r.body && r.body.error) || 'server_error' };
      }
      sent++;
      if (r.status === 200 && r.body) {
        const paid = r.body.awarded || 0;
        awarded += paid;
        // `worth` is what the win was worth before the ceiling; both are kept because "5 of 15" is
        // the sentence, and `capped` is the server's own word for whether it is the one to say.
        settled[q[i].id] = { awarded: paid, worth: r.body.worth != null ? r.body.worth : paid,
                             capped: !!r.body.capped, duplicate: !!r.body.duplicate,
                             alreadyBeaten: !!r.body.alreadyBeaten };
      }
      // 4xx other than 401 is a malformed submission; it will never become well-formed.
    }
    writeQueue(keep);
    return { sent, awarded, settled, kept: 0, ok: true };
  }

  async function summary() {
    try {
      const { body } = await getJson('/api/bouts');
      return {
        ok: true, balance: body.balance || 0, dayTotal: body.dayTotal || 0,
        remaining: body.remaining || 0, cap: body.cap || 0, day: body.day || null,
        // Days in a row with at least one credit, computed by the server from its own clock.
        // Defaulted here rather than left undefined so a page talking to an older deployment
        // renders "no streak" instead of "NaN days".
        streak: body.streak || 0,
        // LIFETIME EARNED, which has been in this response since the store opened and was thrown
        // away here every time. It is not the balance: the balance is earnings minus purchases,
        // and a player who spent forty credits on the season should still be told they earned
        // them. `spent` is deliberately NOT carried — the record panel is about what was done,
        // and what was bought is on the shelf that sold it.
        earned: body.earned || 0,
        // W/L/D, per-deck and per-tier history. NULL, NOT AN EMPTY RECORD, when the field is
        // absent — the two are different facts and the panel treats them differently. A server
        // that has not shipped R1 yet sends no `record` and the panel must not appear at all;
        // a player with no bouts sends zeroes, which is a record that has genuinely started at
        // nothing. Defaulting the absent case to zeroes would draw a "0-0-0" panel against an
        // older deployment and call it a first visit.
        record: body.record || null
      };
    } catch (e) {
      return { ok: false, balance: 0, dayTotal: 0, remaining: 0, cap: 0, day: null, streak: 0,
               earned: 0, record: null };
    }
  }

  return {
    record, flush, summary,
    pending: () => readQueue().length,
    // What the queue is worth beside how many entries it holds. Two numbers because a pitch that
    // says "3 bouts are waiting" is a chore and one that says "up to 17 credits" is an offer.
    queuedTotal,
    // Exported because they are the parts worth testing: one decides who gets paid, the other
    // decides what is reported. Everything else here is plumbing around them.
    playerSeat, submissionFor,
    _readQueue: readQueue,
    _writeQueue: writeQueue
  };
})();
